import { PropsWithChildren, useEffect } from 'react';
import { ContentSegments, ContentSegmentsProps } from '../../components';
import {
  ContentSegmentOption,
  useCurrentSegment,
} from '../../components/segments/ContentSegmentsContext';
import {
  emitCreateFormTableRow,
  emitFocusFormInput,
  listenCreateFormTableRow,
  listenFocusFormInput,
} from '../events';

export interface FormContentSegmentOption<Segment extends string = string>
  extends ContentSegmentOption<Segment> {
  formFieldName?: string | string[];
}

export const FormSegments = <Segment extends string = string>(
  props: ContentSegmentsProps<Segment>
) => {
  const { children, segments, ...rest } = props;
  return (
    <ContentSegments segments={segments} {...rest}>
      <FormSegmentsInner segments={segments}>{children}</FormSegmentsInner>
    </ContentSegments>
  );
};

interface FormSegmentsInnerProps<Segment extends string = string>
  extends PropsWithChildren {
  segments: ContentSegmentsProps<Segment>['segments'];
}

const findMatchingSegment = <Segment extends string = string>(
  segmentValues: Segment[],
  formFieldName: string
): Segment | undefined =>
  segmentValues.find(
    (segmentValue) =>
      formFieldName === segmentValue ||
      formFieldName.startsWith(`${segmentValue}.`)
  );

const FormSegmentsInner = <Segment extends string = string>(
  props: FormSegmentsInnerProps<Segment>
) => {
  const { segments, children } = props;
  const { currentSegment, setCurrentSegment } = useCurrentSegment<Segment>();

  useEffect(() => {
    const segmentValues = segments.map((s) => s.value);

    /**
     * Capture FocusFormInput event bearing form input name and:
     *  1. automatically switch to segment that matches with the beginning of the input name.
     *  2. send focus to that form input.
     */
    const cleanUpFocusFromInput = listenFocusFormInput((event) => {
      const { name } = event.detail;
      const match = findMatchingSegment(segmentValues, name);
      if (match && match !== currentSegment) {
        setCurrentSegment(match);
        /**
         * If segment value was changed, re-emit event to delegate focus to freshly rendered input in this new segment view.
         * Apply timeout:
         *  1. to avoid infinite call loop between this event listener and the event emitter.
         *  2. with a little delay to ensure that the input is rendered before the event is emitted.
         */
        setTimeout(() => emitFocusFormInput(name), 200);
      }
    });

    const cleanUpCreateFormTableRow = listenCreateFormTableRow((event) => {
      const { tableName } = event.detail;
      const match = findMatchingSegment(segmentValues, tableName);
      if (match && match !== currentSegment) {
        setCurrentSegment(match);
        setTimeout(() => emitCreateFormTableRow(tableName), 200);
      }
    });

    return () => {
      cleanUpFocusFromInput();
      cleanUpCreateFormTableRow();
    };
  }, [segments, currentSegment, setCurrentSegment]);

  return children;
};
